import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { keyframes } from "@mui/system";

// Blinking Animation for Price
const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

// Sample service data
const serviceData = [
  {
    title: "Front Bumper Paint",
    warranty: "1 year warranty · NA",
    duration: "Service usually takes up to 1 day",
    features: [
      "Old paint scraping/removal",
      "Minor dents & scratches removal",
      "Anti-rust resistance clear coating",
      "Top-quality primer applied",
      "High quality Nippon/Dupont Paint",
      "Car surface waxing & glazing",
    ],
    price: "₹2999",
    imageUrl: "https://i.ibb.co/86Vz7xr/Front-Bumper-Paint.png",
  },
  {
    title: "Front Fender Paint",
    warranty: "3 year warranty · NA",
    duration: "Service usually takes up to 10 days",
    features: [
      "Old paint scraping/removal",
      "Minor dents & scratches removal",
      "Anti-rust resistance clear coating",
      "Top-quality primer applied",
      "High quality Nippon/Dupont Paint",
      "Car surface waxing & glazing",
    ],
    price: "₹2999",
    imageUrl: "https://i.ibb.co/6nKtsRd/Front-Fender-Paint.png",
  },
  {
    title: "Door Paint",
    warranty: "3 year warranty · NA",
    duration: "Service usually takes up to 10 days",
    features: [
      "Old paint scraping/removal",
      "Minor dents & scratches removal",
      "Anti-rust resistance clear coating",
      "Top-quality primer applied",
      "High quality Nippon/Dupont Paint",
      "Car surface waxing & glazing",
    ],
    price: "₹2999",
    imageUrl: "https://i.ibb.co/DkBSkm6/Door-Paint.png",
  },
  {
    title: "Rear Fender Paint",
    warranty: "3 year warranty · NA",
    duration: "Service usually takes up to 10 days",
    features: [
      "Old paint scraping/removal",
      "Minor dents & scratches removal",
      "Anti-rust resistance clear coating",
      "Top-quality primer applied",
      "High quality Nippon/Dupont Paint",
      "Car surface waxing & glazing",
    ],
    price: "₹2999",
    imageUrl: "https://i.ibb.co/FbYvPGM/Rear-Fender-Paint.png",
  },
  {
    title: "Dicky Paint",
    warranty: "3 year warranty · NA",
    duration: "Service usually takes up to 10 days",
    features: [
      "Old paint scraping/removal",
      "Minor dents & scratches removal",
      "Anti-rust resistance clear coating",
      "Top-quality primer applied",
      "High quality Nippon/Dupont Paint",
      "Car surface waxing & glazing",
    ],
    price: "₹2999",
    imageUrl: "https://i.ibb.co/DkBSkm6/Door-Paint.png",
  },
];

export default function PaintServiceCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % serviceData.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? serviceData.length - 1 : prevIndex - 1
    );
  };

  const currentService = serviceData[currentIndex];

  return (
    <Box sx={{ textAlign: "center", p: { xs: 2, md: 3 }, mt: { xs: 2, md: "20px" } }}>
      <Typography variant="h3" fontWeight="bold" color="orangered" gutterBottom>
        Premium Car Painting & Detailing 🚗✨
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
        Revive Your Car’s Look with High-Quality Paint & Finishing!
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1, md: 3 },
        }}
      >
        {/* Previous Button */}
        <IconButton onClick={handlePrev} sx={{ mb: { xs: 2, md: 0 } }}>
          <ArrowBackIosNewIcon />
        </IconButton>

        {/* Service Card */}
        <Card
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: { xs: "95%", md: "75%" },
            height: { xs: "auto", md: 420 },
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            p: { xs: 2, md: 3 },
          }}
          elevation={3}
        >
          {/* Left Side: Text Content */}
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              p: { xs: 1, md: 2 },
            }}
          >
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color: "#333",
                  pb: 1,
                  borderBottom: "3px solid orangered",
                  display: "inline-block",
                }}
              >
                {currentService.title} -{" "}
                <Typography
                  component="span"
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    color: "orangered",
                    animation: `${blink} 1s infinite`,
                  }}
                >
                  {currentService.price}
                </Typography>
              </Typography>
            </Box>

            {/* Warranty and Duration */}
            <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary", mt: 0.5 }}>
              <Typography variant="body2">{currentService.warranty}</Typography>
              <Box sx={{ mx: 2 }}>|</Box>
              <AccessTimeIcon sx={{ fontSize: 18, mr: 0.5, color: "#ff9800" }} />
              <Typography variant="body2">{currentService.duration}</Typography>
            </Box>

            {/* Features List */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
              <List dense sx={{ p: 0, width: "100%" }}>
                {currentService.features.map((item, idx) => (
                  <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <AutoAwesomeIcon sx={{ color: "orangered", fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: "body2", fontWeight: "bold" }}>
                      {item}
                    </ListItemText>
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>

          {/* Right Side: Image */}
          <CardMedia
            component="img"
            alt="Service Image"
            image={currentService.imageUrl}
            sx={{
              width: { xs: "100%", md: "50%" },
              height: { xs: 200, md: "100%" },
              objectFit: "cover",
              borderRadius: 0,
              mt: { xs: 2, md: 0 },
            }}
          />
        </Card>

        {/* Next Button */}
        <IconButton onClick={handleNext} sx={{ mt: { xs: 2, md: 0 } }}>
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
